import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { get, set, remove } from './Preferences';

// Session tokens (EAM/Portal bearer tokens, Portal API token, ID token) are kept
// in the Android Keystore / iOS Keychain on native builds, via the same
// capacitor-native-biometric credential store that already holds the refresh
// tokens. Capacitor Preferences is plaintext SharedPreferences / UserDefaults.
// Web has no secure store, so it keeps using Preferences as before.
const SERVER_PREFIX = 'com.aptean.eam.token.';

// Preferences keys (see OAuth.ts) whose values are raw tokens. The decoded
// "...Parsed" claim objects are not bearer credentials and stay in Preferences.
const SECURE_TOKEN_KEYS = ['BearerToken', 'BearerTokenPortal', 'PortalApiToken', 'IdToken'];

// Every API call reads the bearer token, so keep an in-memory copy instead of
// decrypting on each request. It also covers reads while the device is locked,
// when the Keystore/Keychain entry can't be decrypted.
const cache = new Map<string, string>();

let migration: Promise<void> | null = null;

const hasSecureStore = () => Capacitor.isNativePlatform();

const serverFor = (key: string) => `${SERVER_PREFIX}${key}`;

const writeNative = async (key: string, value: string): Promise<boolean> => {
  try {
    await NativeBiometric.setCredentials({ server: serverFor(key), username: key, password: value });
    return true;
  } catch (error) {
    console.log('SecureTokenStore: secure write failed for', key, error);
    return false;
  }
};

const readNative = async (key: string): Promise<string | undefined> => {
  try {
    const credentials = await NativeBiometric.getCredentials({ server: serverFor(key) });
    return credentials?.password ?? undefined;
  } catch {
    // No entry stored (or not readable right now).
    return undefined;
  }
};

const deleteNative = async (key: string): Promise<void> => {
  try {
    await NativeBiometric.deleteCredentials({ server: serverFor(key) });
  } catch (error) {
    console.log('SecureTokenStore: secure delete failed for', key, error);
  }
};

/**
 * Moves tokens written by older builds out of plaintext Preferences into the
 * secure store (once per app launch). A token is only removed from Preferences
 * after it was stored securely, so a failed write never logs the user out.
 */
const migrateLegacyTokens = (): Promise<void> => {
  if (migration == null) {
    migration = (async () => {
      for (const key of SECURE_TOKEN_KEYS) {
        const legacy = await get(key);
        if (legacy === undefined) continue;
        if (await writeNative(key, legacy)) {
          cache.set(key, legacy);
          await remove(key);
        }
      }
    })();
  }
  return migration;
};

export const setSecureToken = async (key: string, value: string): Promise<void> => {
  if (!hasSecureStore()) {
    await set(key, value);
    return;
  }
  await migrateLegacyTokens();
  cache.set(key, value);
  if (await writeNative(key, value)) {
    // Never leave a plaintext copy behind next to the secure one.
    await remove(key);
  } else {
    // Keystore/Keychain unavailable on this device: keep the session working.
    await set(key, value);
  }
};

export const getSecureToken = async (key: string): Promise<string | undefined> => {
  if (!hasSecureStore()) {
    return await get(key);
  }
  await migrateLegacyTokens();
  if (cache.has(key)) {
    return cache.get(key);
  }
  const value = (await readNative(key)) ?? (await get(key));
  if (value !== undefined) {
    cache.set(key, value);
  }
  return value;
};

export const removeSecureToken = async (key: string): Promise<void> => {
  cache.delete(key);
  await remove(key);
  if (hasSecureStore()) {
    await deleteNative(key);
  }
};

/** Removes every registered session token (used alongside Preferences.clear()). */
export const clearSecureTokens = async (): Promise<void> => {
  for (const key of SECURE_TOKEN_KEYS) {
    await removeSecureToken(key);
  }
};
